if (!customElements.get('product-request-quote')) {
  class ProductRequestQuote extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.scope = this.closest('.shopify-section') || this.parentElement;
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-quote-submit]');
      this.hint = this.querySelector('[data-quote-hint]');
      this.progress = this.querySelector('[data-quote-progress]');
      if (!this.form || !this.button || !this.scope) return;
      this.restoreResult();
      this.setReturnUrl();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.validator = document.createElement('input');
      this.validator.type = 'email';
      this.validator.required = true;
      try {
        this.variants = JSON.parse(this.querySelector('[data-quote-variants]').textContent);
      } catch (_) {
        this.variants = [];
      }
      const refresh = () => this.update();
      this.scope.addEventListener('input', refresh, options);
      this.scope.addEventListener('change', refresh, options);
      // Fill contact fields before CAPTCHA or another form listener reads them.
      this.button.addEventListener('click', (event) => this.submit(event), { ...options, capture: true });
      this.form.addEventListener('submit', (event) => this.submit(event), { ...options, capture: true });
      this.button.addEventListener('keydown', (event) => {
        if (this.loading && ['Enter', ' '].includes(event.key)) event.preventDefault();
      }, options);
      window.addEventListener('pageshow', () => {
        this.setLoading(false);
        this.update();
      }, options);
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.scope, { childList: true, subtree: true });
      // Covers delayed app loading and autofill that doesn't dispatch input events.
      this.timer = window.setInterval(() => this.update(), 700);
      this.update();
      const result = this.querySelector('[data-quote-success]:not([hidden]), [data-quote-server-error]');
      if (result) result.focus();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      clearInterval(this.timer);
      clearTimeout(this.loadingTimer);
    }

    setLoading(loading, message = '') {
      this.loading = loading;
      // Use the theme's existing spinner. Never disable a valid button here:
      // CAPTCHA may resubmit the form using this same submit button.
      this.button.classList.toggle('is-loading', loading);
      if (loading) this.button.setAttribute('aria-busy', 'true');
      else this.button.removeAttribute('aria-busy');
      if (this.progress) {
        if (this.progress.textContent !== message) this.progress.textContent = message;
        this.progress.hidden = !message;
      }
      if (!loading) clearTimeout(this.loadingTimer);
    }

    startLoading() {
      if (this.loading) return;
      this.setLoading(true, 'Submitting your request. Please complete verification if prompted.');
      this.loadingTimer = window.setTimeout(() => {
        this.setLoading(false, 'Submission is taking longer than expected. Please complete any verification, or check whether your request was received before trying again.');
        this.update();
      }, 45000);
    }

    setReturnUrl() {
      // Preserve view=..., preview_theme_id, variant and all current query parameters.
      // Shopify follows return_to on a successful contact submission.
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.set('contact_posted', 'true');
      url.searchParams.set('quote_form', this.form.id);
      const input = this.form.elements.namedItem('return_to');
      if (input) input.value = url.pathname + url.search;
    }

    restoreResult() {
      const url = new URL(window.location.href);
      const success = this.querySelector('[data-quote-success]');
      const matches = url.searchParams.get('contact_posted') === 'true'
        && url.searchParams.get('quote_form') === this.form.id;
      let attempted = false;
      try {
        attempted = sessionStorage.getItem(`product-quote:${this.form.id}`) === 'submitted';
      } catch (_) { /* Liquid server success remains available. */ }
      if (success && matches && (attempted || success.hasAttribute('data-quote-server-success'))) {
        success.hidden = false;
        try { sessionStorage.removeItem(`product-quote:${this.form.id}`); } catch (_) {}
        // Clean only the quote result flags; keep the original product/template URL.
        url.searchParams.delete('contact_posted');
        url.searchParams.delete('quote_form');
        url.hash = '';
        window.history.replaceState(window.history.state, '', url.pathname + url.search);
      }
    }

    optionApps() {
      return [...this.scope.querySelectorAll('ymq-option')].filter((app) => {
        const productId = app.getAttribute('ymq-option-product-id');
        return !productId || productId === this.dataset.productId;
      });
    }

    activeBox(box) {
      return Boolean(box && !box.closest('[hidden], .ymq-option-condition-hide')
        && box.getClientRects().length
        && getComputedStyle(box).visibility !== 'hidden');
    }

    contactField(selector, fallback) {
      return this.field(selector) || this.field(fallback);
    }

    field(selector) {
      if (!selector) return null;
      try {
        return [...this.scope.querySelectorAll(selector)].find((field) => {
          const app = field.closest('ymq-option');
          return 'value' in field && !field.matches(':disabled') && field.getClientRects().length > 0
            && !field.closest('[hidden], .ymq-option-condition-hide')
            && (!app || !app.getAttribute('ymq-option-product-id')
              || app.getAttribute('ymq-option-product-id') === this.dataset.productId);
        }) || null;
      } catch (_) {
        return null;
      }
    }

    email() {
      const input = this.contactField(this.dataset.emailSelector, 'ymq-option .ymq-option-email');
      const value = input?.value.trim() || '';
      this.validator.value = value;
      return { input, value, valid: Boolean(input && value && this.validator.validity.valid) };
    }

    update() {
      // Also wire up CAPTCHA when the theme editor replaces this section.
      if (!this.captchaWired && window.Shopify?.captcha?.protect) {
        this.captchaWired = true;
        try {
          window.Shopify.captcha.protect(this.form);
        } catch (_) {
          this.captchaWired = false;
        }
      }
      const email = this.email();
      const disabled = !email.valid;
      if (this.button.disabled !== disabled) this.button.disabled = disabled;
      if (this.hint.hidden !== email.valid) this.hint.hidden = email.valid;
      this.form.elements.namedItem('contact[email]').value = email.valid ? email.value : '';
      if (email.valid) this.writePayload(email);
    }

    phoneValue(input) {
      if (!input || !input.value.trim()) return '';
      // Prefer the app's international phone widget when its public API exists.
      try {
        const widget = window.intlTelInput?.getInstance?.(input)
          || window.intlTelInputGlobals?.getInstance?.(input);
        const number = widget?.getNumber?.();
        if (number) return number;
      } catch (_) { /* Use the displayed number and country prefix below. */ }
      const value = input.value.trim();
      if (value.startsWith('+')) return value;
      const wrapper = input.closest('.iti');
      const prefix = wrapper?.querySelector('.iti__selected-dial-code')?.textContent.trim()
        || wrapper?.querySelector('[aria-selected="true"][data-dial-code]')?.getAttribute('data-dial-code');
      return prefix ? `${prefix.startsWith('+') ? prefix : `+${prefix}`} ${value}` : value;
    }

    optionEntries() {
      const entries = [];
      const seen = new Set();
      const apps = this.optionApps();
      for (const app of apps) {
        const productId = app.getAttribute('ymq-option-product-id');
        if (productId && productId !== this.dataset.productId) continue;
        for (const box of app.querySelectorAll('.ymq-options-box')) {
          if (!this.activeBox(box)) continue;
          const label = box.getAttribute('ymq-option-label') || box.querySelector('.ymq_lable')?.textContent.trim() || 'Option';
          const values = [];
          for (const input of box.querySelectorAll('input, select, textarea')) {
            // Only YMQ value controls: the phone country search is also an input.
            if (!input.hasAttribute('ymq-option-value-id') || input.matches(':disabled')
              || input.closest('.ymq-options-box') !== box
              || input.matches('.ymq-option-email, .ymq-option-tel')) continue;
            if (['hidden', 'submit', 'button', 'file'].includes(input.type)) continue;
            if (['checkbox', 'radio'].includes(input.type) && !input.checked) continue;
            // Radio inputs may be visually hidden by YMQ's button styling.
            if (!['checkbox', 'radio'].includes(input.type)
              && (!input.getClientRects().length || getComputedStyle(input).visibility === 'hidden')) continue;
            if (input.tagName === 'SELECT') {
              values.push(...[...input.selectedOptions].filter((option) => option.value).map((option) => option.textContent.trim()));
            } else if (input.value.trim()) {
              values.push(input.value.trim());
            }
          }
          if (!values.length) continue;
          const value = [...new Set(values)].join(', ');
          const key = JSON.stringify([label, value]);
          if (!seen.has(key)) { seen.add(key); entries.push({ label, value }); }
        }
      }
      return entries;
    }

    validateOptions() {
      this.optionValidationMessage = '';
      const apps = this.optionApps();
      const ymq = window.ymqOption;
      // Verified against the YMQ script loaded by this product preview.
      // These are plugin implementation methods, so fail closed if an app
      // update changes them. Never replace YMQ errors with our own markup.
      if (!apps.length || typeof ymq?.buildFormData !== 'function'
        || apps.some((app) => typeof app._getBlockConfig !== 'function')) {
        this.optionValidationMessage = 'Product options are still loading. Please wait a moment and try again.';
        return false;
      }
      let valid = true;
      try {
        for (const app of apps) {
          // YMQ's validateScroll() uses this same call. The third argument
          // displays native errors, including conditional text fields.
          // Do not call validateScroll(): scrolling and shaking are separate.
          ymq.buildFormData(app, null, true);
          if (app._getBlockConfig('validate') !== true) valid = false;
        }
      } catch (error) {
        console.error('Request quote: YMQ validation failed.', error);
        this.optionValidationMessage = 'Product options could not be validated. Please refresh the page and try again.';
        return false;
      }
      // return valid;
            if (!valid && typeof ymq.scrollElement === 'function') {
        const errors = apps.flatMap((app) => [
          ...app.querySelectorAll(
            '.ymq-option-box-error, .ymq-option-group-box-error'
          )
        ]);

        if (errors.length) ymq.scrollElement(errors);
      }

      return valid;
    }

    submit(event) {
      const email = this.email();
      if (!email.valid) {
        event.preventDefault();
        this.setLoading(false);
        this.update();
        if (email.input && !email.valid) {
          email.input.focus();
          email.input.reportValidity?.();
        }
        return;
      }
      if (!this.validateOptions()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.setLoading(false, this.optionValidationMessage);
        return;
      }
      this.setReturnUrl();
      this.writePayload(email);
      this.startLoading();
      try { sessionStorage.setItem(`product-quote:${this.form.id}`, 'submitted'); } catch (_) {}
      // Do not lock this form on submit: Shopify may dispatch submit again
      // after CAPTCHA verification. A valid second submit must be allowed.
    }

    writePayload(email) {
      const phone = this.phoneValue(this.contactField(this.dataset.phoneSelector, 'ymq-option .ymq-option-tel'));
      const name = this.field('ymq-option [ymq-option-label="Full Name"] input[ymq-option-value-id]')?.value.trim() || '';
      const productForm = this.scope.querySelector('.js-product-form-main');
      const variantId = productForm?.querySelector('[name="id"]')?.value
        || new URL(location.href).searchParams.get('variant');
      const variant = this.variants.find((item) => String(item.id) === String(variantId));
      const quantityInput = productForm?.querySelector('[name="quantity"]')
        || this.scope.querySelector('quantity-input [name="quantity"]');
      // Submit each detail once as its own Shopify contact field. Shopify's
      // notification template renders these field labels separately from values.
      const entries = [];
      if (variant) {
        if (variant.title && variant.title !== 'Default Title') {
          entries.push({ label: 'Variant', value: variant.title });
        }
        entries.push({ label: 'Variant ID', value: String(variant.id) });
        if (variant.sku) entries.push({ label: 'SKU', value: variant.sku });
      }
      const options = this.optionEntries();
      // Order Quantity in the app is the requested quantity; avoid showing
      // the theme's default quantity of 1 beside it.
      if (quantityInput?.value && !options.some(({ label }) => /order quantity/i.test(label))) {
        entries.push({ label: 'Quantity', value: quantityInput.value });
      }
      for (const entry of options) {
        if (entry.label.trim().toLowerCase() === 'full name') continue;
        entries.push({ ...entry, label: entry.label.replace(/Pringting/gi, 'Printing') });
      }
      this.form.elements.namedItem('contact[email]').value = email.value;
      const nameField = this.form.elements.namedItem('contact[name]');
      if (nameField) nameField.value = name;
      const phoneField = this.form.elements.namedItem('contact[phone]');
      phoneField.value = phone;
      phoneField.disabled = !phone;
      const details = this.form.querySelector('[data-quote-details]');
      const signature = JSON.stringify(entries);
      if (details && signature !== this.payloadSignature) {
        const fragment = document.createDocumentFragment();
        const used = new Set(['request_type', 'name', 'email', 'phone', 'product', 'product_url', 'body']);
        for (const { label, value } of entries) {
          const base = label.replace(/\[/g, '(').replace(/\]/g, ')').trim() || 'Option';
          let key = base;
          let suffix = 2;
          while (used.has(key.toLowerCase())) key = `${base} (${suffix++})`;
          used.add(key.toLowerCase());
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `contact[${key}]`;
          input.value = value;
          fragment.append(input);
        }
        this.payloadSignature = signature;
        details.replaceChildren(fragment);
      }
      // The native Shopify contact form handles delivery, errors and CAPTCHA.
    }
  }
  customElements.define('product-request-quote', ProductRequestQuote);
}
